import {
  ADJUSTMENT_KIND_LABEL,
  type AdjustmentKind,
  type AdjustmentMode,
  type PaymentStatus,
  type Tone,
} from '@/domain';
import { formatMoney } from '@/shared/format/money';

/**
 * What the forms and pages of a case's money share. The request of
 * 8 October 2026 (fourth).
 */

/** A typed amount, to the cent, or null when it is not one: empty, or not a number. */
export function parseAmount(text: string): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
}

/**
 * A discount or a charge as a heading says it: "Discount 10%", "Added charge
 * AED 15". `money` says an amount as the screen does.
 */
export function adjustmentHeading(
  kind: AdjustmentKind,
  mode: AdjustmentMode,
  value: number,
  money: (value: number) => string = formatMoney,
): string {
  return `${ADJUSTMENT_KIND_LABEL[kind]} ${mode === 'percent' ? `${formatMoney(value)}%` : money(value)}`;
}

/** The same in the middle of a sentence: "a discount of 10%", "a charge of AED 15". */
export function adjustmentInWords(
  kind: AdjustmentKind,
  mode: AdjustmentMode,
  value: number,
  money: (value: number) => string = formatMoney,
): string {
  const figure = mode === 'percent' ? `${formatMoney(value)}%` : money(value);
  return kind === 'discount' ? `a discount of ${figure}` : `a charge of ${figure}`;
}

/** The tone each state of a case's money is drawn in. Unpaid is quiet: most cases are, most of the time. */
export const PAYMENT_STATUS_TONE: Record<PaymentStatus, Tone> = {
  unpaid: 'idle',
  part: 'watch',
  paid: 'ok',
};

/** A signed sum: a discount reads with its minus, a charge with a plus. `money` says the amount as the screen does. */
export function signedMoney(amount: number, money: (value: number) => string = formatMoney): string {
  return amount < 0 ? `-${money(-amount)}` : `+${money(amount)}`;
}
